import { Component, ElementRef, Input, NgZone, ViewChild, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, CommunitiesService, CommunityChannel, CommunityService, FSP_COMMUNITY_STRING_ID, UserService, WindowService, WorkspaceService, environment } from "@mypxplat/xplat/core";
import { AppService, BetaService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FileUploader } from "ng2-file-upload";
import { take, takeUntil } from "rxjs/operators";
import { DatePickerComponent, WorkspaceUploadComponent } from "..";
import { MessageInputComponent } from "../../message-input/message-input.component";
import { SearchContentComponent } from "../search-content/search-content.component";

/** The built-in views every channel list carries — not channels a post can be tagged with. */
const CHANNEL_VIEW_IDS = ["all_posts", "my_posts", "pinned_posts"];

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-edit-comment",
  templateUrl: "edit-comment.component.html",
  standalone: false,
})
export class EditCommentComponent extends BaseComponent {
  @ViewChild("messageSpan") messageSpan: ElementRef;
  @ViewChild("editPostInput") editPostInput: MessageInputComponent;
  @ViewChild("editSubjectInput") editSubjectInput: MessageInputComponent;
  @Input() comment: any;
  @Input() isCommunityPost: any;
  @Input() isHtmlComment: any;
  @Input() collaborators: any;
  @Input() isVideoNote: any;
  @Input() isBetaComment: boolean = false;
  public shouldShowChannelSelector: boolean = false;
  public message: string;
  public files: Array<any>;
  public uploadingFiles: Array<any>;
  public uploadedAttachments: Array<any>;
  public uploader: FileUploader;
  public hasBaseDropZoneOver: any;
  public editedPostContent: any;
  public editedSubjectContent: any;
  public scheduled = false;
  // Never set in this component (template copied from the post composer) — always undefined.
  public queued: boolean;
  public features = environment.features;
  /**
   * The channels the post can be tagged with: its own community's (not the old global skills
   * list), less the views, archived ones and any this member can't post in — what the composer
   * offers — and less the ones already on it. Set by `_loadChannels`; beta posts use their tags.
   */
  public channelOptions: CommunityChannel[] = [];
  /** Channel titles by id, for the chips. */
  public channelTitles: Record<string, any> = {};
  public channelsReady = false;
  private _channels: CommunityChannel[] = [];
  private _communityId = "";

  constructor(
    userService: UserService,
    private _win: WindowService,
    private _zone: NgZone,
    public activeModal: NgbActiveModal,
    public workspaceService: WorkspaceService,
    public communityService: CommunityService,
    public appService: AppService,
    public betaService: BetaService,
    private _webCommunityService: WebCommunityService,
    private _communitiesService: CommunitiesService
  ) {
    super(userService);
  }

  ngOnInit() {
    if (this.comment.subject) {
      this.editedSubjectContent = this.comment.subject;
    }
    this.uploader = new FileUploader({
      isHTML5: true,
      method: "PUT",
      queueLimit: 1,
      autoUpload: false,
      disableMultipart: true,
      url: "",
    });
    if (this.isCommunityPost && !this.isBetaComment && !this.comment.parentID) this._loadChannels();
    this.message = (this.comment.message || this.comment.text) + "";
    this.editedPostContent = this.message;
    if (this.comment.files) this.files = [...this.comment.files];
    if (this.comment.scheduled_date) this.scheduled = true;
    if (this.isCommunityPost || this.isHtmlComment) {
      this.comment = this.communityService.buildHtmlComment({ ...this.comment }, true, true);
    }
  }

  ngAfterViewInit() {
    if (this.isCommunityPost || this.isHtmlComment) {
      this._win.setTimeout(() => {
        // The span editor gets the rendered HTML, not the stored text: since #1728 a message
        // is plain text, and a contenteditable span shows its "\n"s as spaces, so every line
        // ran together on edit. The rendered form also carries the mention chips
        // `finalizeMessage` reads back on save. Quill (beta) messages are already HTML.
        const editorValue = this.isBetaComment ? this.comment.message : (this.comment.htmlMessage ?? this.comment.message);
        this.editPostInput.setMessageValue(editorValue, false);
        this._win.setTimeout(() => {
          if (this.isBetaComment && this.editSubjectInput) this.editSubjectInput.setMessageValue(this.comment.subject, false);
        }, 100);
      }, 50);
    } else {
      if ((this.comment.message || this.comment.text) && this.messageSpan) this.messageSpan.nativeElement.innerText = this.comment.message || this.comment.text;
    }
  }

  addChannel(channel) {
    this.shouldShowChannelSelector = false;
    if (!this.comment.channels || this.comment.channels.length < 3) {
      this.comment.channels = [...(this.comment.channels || []), channel.string_id];
    }
    this._refreshChannelOptions();
  }

  removeChannel(string_id) {
    this.comment.channels = this.comment.channels.filter((item) => item != string_id);
    this._refreshChannelOptions();
  }

  toggleChannelSelector() {
    this.shouldShowChannelSelector = !this.shouldShowChannelSelector;
    // Focus into the list, so Esc reaches it (and closes just it) rather than the modal.
    if (this.shouldShowChannelSelector) this._win.setTimeout(() => document.getElementById("edit-channel-list")?.focus());
  }

  /** Esc closes the channel list without closing the whole edit modal. */
  onChannelSelectorEscape(event: Event) {
    event.stopPropagation();
    this.shouldShowChannelSelector = false;
  }

  /** The post's own community's channels — cached by the service, so usually instant. */
  private _loadChannels() {
    this._communityId = this.comment.community_string_id || FSP_COMMUNITY_STRING_ID;
    this._webCommunityService
      .getCommunityChannels(this._communityId)
      .pipe(take(1))
      .subscribe({
        next: (channels) => {
          this._channels = (channels || []).filter((c) => c?.string_id && !CHANNEL_VIEW_IDS.includes(c.string_id) && !c.archived);
          const titles: Record<string, any> = {};
          this._channels.forEach((c) => (titles[c.string_id] = c.title));
          this.channelTitles = titles;
          this.channelsReady = true;
          this._refreshChannelOptions();
        },
        error: () => (this.channelsReady = true),
      });
  }

  private _refreshChannelOptions() {
    const tagged = this.comment.channels || [];
    const user = this.userService.user;
    this.channelOptions = this._channels.filter((c) => !tagged.includes(c.string_id) && this._communitiesService.canPostInChannel(user, this._communityId, c));
  }

  removeAttachment(file) {
    this.files = this.files.filter((item) => file.id != item.id);
  }

  removeArticle(article) {
    this.comment.articles = this.comment.articles.filter((item) => item.article.id != article.article.id);
  }

  removeLearnContent(content) {
    this.comment.learn_content = this.comment.learn_content.filter((item) => item.content.id != content.content.id);
  }

  removeEmbed(embed) {
    this.comment.embeds = this.comment.embeds.filter(({ id }) => id != embed.id);
  }

  keyUpEvent(args) {
    this.message = this.messageSpan.nativeElement.innerText;
  }

  contentPasted() {
    this.message = this.messageSpan.nativeElement.innerText;
  }

  focusOut(args) {
    this.message = this.messageSpan.nativeElement.innerText;
  }

  droppedFile(args) {
    this.attachFile(args);
    this.uploader.clearQueue();
  }

  fileOverBase(e) {
    this.hasBaseDropZoneOver = e;
  }

  attachFile(droppedFile = false) {
    const modalRef = this.appService.showModal(WorkspaceUploadComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalRef.componentInstance.isPostAttachment = true;
    if (droppedFile) modalRef.componentInstance.droppedFile = droppedFile;
    modalRef.componentInstance.uploadProgressChanged.pipe().subscribe((result) => {
      this.uploadingFiles.forEach((item) => {
        if (item.id == result.file.id) item.progress = result.progress;
      });
    });
    modalRef.componentInstance.fileCreated.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (!this.uploadingFiles) this.uploadingFiles = [];
      this.uploadingFiles.push(result);
    });
    modalRef.componentInstance.fileUploadFinished.subscribe((result) => {
      this._zone.run(() => {
        if (!this.files) this.files = [];
        result.url = this.communityService.buildFileUrl(result.user_id, result);
        this.files.push(result);
        this.uploadingFiles = this.uploadingFiles.filter((item) => item.id != result.id);
      });
    });
  }

  showAttach(type) {
    // Scrollable: the results scroll inside the body between a fixed header and footer, the
    // layout that scrolls reliably by touch on phones (the whole-content scroller didn't).
    const modalRef = this.appService.showModal(SearchContentComponent, { size: "lg", scrollable: true });
    modalRef.componentInstance.type = type;
    modalRef.componentInstance.contentSelected.pipe(take(1)).subscribe((result) => {
      if (type == "articles") {
        if (!this.comment.articles) this.comment.articles = [];
        this.comment.articles.push({ selector: false, editorSelector: false, article: result });
      } else {
        if (!this.comment.learn_content) this.comment.learn_content = [];
        this.comment.learn_content.push({ selector: false, editorSelector: false, content: result });
      }
    });
  }

  contentDetected(args) {
    if (args.article) {
      if (!this.comment.articles) this.comment.articles = [];
      this.comment.articles.push(args);
    } else if (args.content) {
      if (!this.comment.learn_content) this.comment.learn_content = [];
      this.comment.learn_content.push(args);
    }
  }

  mentionSelected(args) {
    if (!this.comment.mentions) this.comment.mentions = [];
    this.comment.mentions.push(args);
  }

  close() {
    if (this.uploadingFiles && this.uploadingFiles.length) {
      alert("Please wait until your files finish uploading.");
    } else {
      this.activeModal.close();
    }
  }

  postContentChanged(args) {
    this.editedPostContent = args;
  }

  postSubjectChanged(args) {
    this.editedSubjectContent = args;
  }

  save() {
    if (this.uploadingFiles && this.uploadingFiles.length) {
      alert("Please wait until your files finish uploading.");
    } else {
      if (this.messageSpan) this.comment[this.comment.text ? "text" : "message"] = this.message;
      if (this.files || this.comment.files) this.comment.files = this.files;

      if (this.isCommunityPost || this.isHtmlComment) {
        let messageContent = this.editedPostContent;
        if (this.comment.articles) {
          this.comment.articles.forEach((item) => {
            if (item.editorSelector) {
              item.editorSelector = item.editorSelector.replace("&nbsp;", "");
              messageContent = messageContent.replace(item.editorSelector, item.selector + " ");
            }
          });
        }
        if (this.comment.learn_content) {
          this.comment.learn_content.forEach((item) => {
            if (item.editorSelector) {
              item.editorSelector = item.editorSelector.replace("&nbsp;", "");
              messageContent = messageContent.replace(item.editorSelector, item.selector + " ");
            }
          });
        }
        if (this.comment.categories_or_section) {
          this.comment.categories_or_section.forEach((item) => {
            if (item.editorSelector) {
              item.editorSelector = item.editorSelector.replace("&nbsp;", "");
              messageContent = messageContent.replace(item.editorSelector, item.selector + " ");
            }
          });
        }
        // Mentions resolve last: finalizeMessage measures its character offsets
        // against the string it returns, and rebuilds the record list from what
        // is actually still in the editor -- so a mention the user deleted while
        // editing stops being notified, which the old replace pass never handled.
        if (this.editPostInput) {
          const finalized = this.editPostInput.finalizeMessage(messageContent, this.comment.mentions);
          messageContent = finalized.message;
          this.comment.mentions = finalized.mentions;
        }
        if (this.comment.message != messageContent) {
          if (!this.comment.edits) this.comment.edits = [];
          this.comment.edits.push(this.comment.message);
        }
        this.comment.message = messageContent;
        if (this.isBetaComment) this.comment.subject = this.editedSubjectContent;
      }

      this.activeModal.close(this.comment);
    }
  }

  onScheduleChange(event: Event): void {
    if (!this.scheduled) {
      this.showDatePicker();
    } else {
      this.scheduled = false;
      this.comment.scheduled_date = new Date();
      this.comment.created = new Date();
    }
  }

  showDatePicker() {
    let modalInstance = this.appService.showModal(DatePickerComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalInstance.componentInstance.past = false;
    modalInstance.componentInstance.earliestYear = new Date().getFullYear();
    modalInstance.componentInstance.includeTimePicker = true;
    modalInstance.componentInstance.dateSelected.subscribe((date) => {
      if (date < new Date()) {
        alert("You cannot schedule a post in the past.");
        return;
      }
      this.scheduled = true;
      this.comment.created = date;
      this.comment.scheduled_date = date;
    });
    modalInstance.dismissed.subscribe(() => {
      if (!this.comment.scheduled_date) {
        this.scheduled = false;
      }
    });
    modalInstance.closed.subscribe(() => {
      if (!this.comment.scheduled_date) {
        this.scheduled = false;
      }
    });
  }
}
